import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { imgUrl } from "@/lib/api";

const DEFAULT = { id: "default", style: "text", title: "YOUR HOBBY, YOUR STORY", subtitle: "Temukan koleksi model kit, figure, diecast, dan berbagai mainan hobi favoritmu.", cta_text: "Jelajahi Katalog", cta_link: "/katalog", cta2_text: "Lihat Pre-Order", cta2_link: "/pre-order" };

export function SmartLink({ to, children, ...rest }) {
  if (!to) return <div {...rest}>{children}</div>;
  if (to.startsWith("/")) return <Link to={to} {...rest}>{children}</Link>;
  return <a href={to} target="_blank" rel="noreferrer" {...rest}>{children}</a>;
}

function ImageSlide({ b, i }) {
  return (
    <SmartLink to={b.link} className="block relative bg-neutral-900" data-testid={`hero-slide-link-${i}`} aria-label={b.title || "Banner"}>
      <picture>
        {b.image_mobile && <source media="(max-width: 640px)" srcSet={imgUrl(b.image_mobile)} />}
        <img src={imgUrl(b.image_desktop)} alt={b.title || "Banner Bengkel Toys & Hobbies"} loading={i === 0 ? "eager" : "lazy"}
          className={`w-full object-cover ${b.image_mobile ? "aspect-[4/5] sm:aspect-[3/1]" : "aspect-[16/9] sm:aspect-[3/1]"}`} />
      </picture>
    </SmartLink>
  );
}

function TextSlide({ b, i }) {
  const words = (b.title || "").split(",");
  const Heading = i === 0 ? "h1" : "h2";
  return (
    <div className="relative overflow-hidden bg-[var(--bk-yellow)] h-full">
      <div className="absolute inset-0 halftone opacity-60" />
      <div className="absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full bg-[var(--bk-orange)]" />
      <div className="absolute right-[30%] bottom-[-120px] h-[260px] w-[260px] rounded-full bg-[var(--bk-blue)] hidden lg:block" />
      {b.link && <SmartLink to={b.link} className="absolute inset-0 z-[1]" data-testid={`hero-slide-link-${i}`} aria-label={b.title} />}
      <div className="relative container-x grid lg:grid-cols-12 gap-8 items-center py-10 sm:py-14 lg:py-20">
        <div className="lg:col-span-6 xl:col-span-7">
          <span className="inline-flex items-center gap-2 bg-white border-2 border-neutral-900 rounded-full px-3 py-1 text-xs font-bold">
            <Sparkles className="h-3.5 w-3.5 text-[var(--bk-red)]" /> Toko Hobi di Pontianak
          </span>
          <Heading className="font-display text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-7xl mt-5 text-neutral-900">
            {words.map((w, k) => (
              <span key={k} className={k === 1 ? "block text-white [text-shadow:3px_3px_0_#111]" : "block"}>{w.trim()}{k < words.length - 1 ? "," : ""}</span>
            ))}
          </Heading>
          {b.subtitle && <p className="mt-5 text-base sm:text-lg max-w-xl text-neutral-900/85 font-medium">{b.subtitle}</p>}
          <div className="relative z-[2] mt-8 flex flex-wrap gap-3">
            {b.cta_text && <SmartLink to={b.cta_link || "/katalog"} className="btn-ink" data-testid={i === 0 ? "hero-catalog-btn" : `hero-cta1-${i}`}>{b.cta_text} <ArrowRight className="h-4 w-4" /></SmartLink>}
            {b.cta2_text && <SmartLink to={b.cta2_link || "/pre-order"} className="btn-brand bg-white" data-testid={i === 0 ? "hero-po-btn" : `hero-cta2-${i}`}>{b.cta2_text}</SmartLink>}
          </div>
        </div>
        <div className="lg:col-span-6 xl:col-span-5 relative h-[300px] sm:h-[380px] lg:h-[440px]">
          {b.image_desktop ? (
            <picture>
              {b.image_mobile && <source media="(max-width: 640px)" srcSet={imgUrl(b.image_mobile)} />}
              <img src={imgUrl(b.image_desktop)} alt={b.title} className="absolute inset-0 h-full w-full object-cover rounded-3xl sticker" />
            </picture>
          ) : (
            <>
              <img src="/demo/gunpla-2.webp" alt="" className="absolute right-0 top-2 w-[46%] aspect-square object-cover rounded-2xl sticker rotate-[5deg]" />
              <img src="/demo/figure-1.webp" alt="" className="absolute left-0 bottom-0 w-[42%] aspect-square object-cover rounded-2xl sticker rotate-[-6deg]" />
              <img src="/demo/diecast-1.webp" alt="" className="absolute right-[6%] bottom-[-4%] w-[34%] aspect-square object-cover rounded-2xl sticker rotate-[3deg] hidden sm:block" />
              <img src="/brand/logo.webp" alt="Bengkel Toys & Hobbies" className="floaty absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[62%] max-w-[300px] drop-shadow-[6px_8px_0_rgba(0,0,0,0.25)]" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function HeroSlider({ banners }) {
  const slides = banners?.length ? banners : [DEFAULT];
  const [ref, embla] = useEmblaCarousel({ loop: slides.length > 1 });
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const onSelect = useCallback(() => { if (embla) setIdx(embla.selectedScrollSnap()); }, [embla]);
  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    return () => { embla.off("select", onSelect); };
  }, [embla, onSelect]);
  useEffect(() => {
    if (!embla || slides.length < 2 || paused) return;
    const t = setInterval(() => embla.scrollNext(), 6000);
    return () => { clearInterval(t); };
  }, [embla, slides.length, paused]);

  return (
    <section className="relative border-b-2 border-neutral-900" data-testid="hero-banner" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="overflow-hidden" ref={ref}>
        <div className="flex">
          {slides.map((b, i) => (
            <div key={b.id || i} className="min-w-0 shrink-0 grow-0 basis-full" data-testid={`hero-slide-${i}`}>
              {b.style === "image" && b.image_desktop ? <ImageSlide b={b} i={i} /> : <TextSlide b={b} i={i} />}
            </div>
          ))}
        </div>
      </div>
      {slides.length > 1 && (
        <>
          <button onClick={() => embla?.scrollPrev()} aria-label="Banner sebelumnya" data-testid="hero-prev" className="hidden sm:grid absolute left-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 place-items-center rounded-full bg-white border-2 border-neutral-900 hover:bg-[var(--bk-yellow)] transition-colors"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={() => embla?.scrollNext()} aria-label="Banner berikutnya" data-testid="hero-next" className="hidden sm:grid absolute right-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 place-items-center rounded-full bg-white border-2 border-neutral-900 hover:bg-[var(--bk-yellow)] transition-colors"><ChevronRight className="h-5 w-5" /></button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-2 rounded-full bg-white/90 border-2 border-neutral-900 px-3 py-1.5">
            {slides.map((_, i) => (
              <button key={i} onClick={() => embla?.scrollTo(i)} aria-label={`Banner ${i + 1}`} data-testid={`hero-dot-${i}`} className={`h-2.5 rounded-full transition-[width,background-color] ${i === idx ? "w-6 bg-[var(--bk-orange)]" : "w-2.5 bg-neutral-400"}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
